import React from 'react'
import { 
  Box, 
  Alert,
  AlertTitle,
  Button,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Collapse,
  IconButton
} from '@mui/material'
import { 
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugReportIcon,
  NetworkCheck as NetworkIcon
} from '@mui/icons-material'
import { poolwerxColors } from '../../../theme/colors'

// ============================================================================
// Types
// ============================================================================

export type ErrorSeverity = 'error' | 'warning' | 'info'
export type ErrorType = 'network' | 'api' | 'validation' | 'permission' | 'generic'

export interface ErrorStateProps {
  /** Error message to display */
  message?: string
  /** Error title/heading */
  title?: string
  /** Error severity level */
  severity?: ErrorSeverity
  /** Type of error for better UX */
  errorType?: ErrorType
  /** Detailed error information */
  details?: string
  /** Error code or ID */
  errorCode?: string | number
  /** Show retry button */
  showRetry?: boolean
  /** Retry button text */
  retryText?: string
  /** Retry button handler */
  onRetry?: () => void
  /** Show error details toggle */
  showDetails?: boolean
  /** Custom action buttons */
  actions?: Array<{
    label: string
    onClick: () => void
    variant?: 'contained' | 'outlined' | 'text'
    color?: 'primary' | 'secondary' | 'error' | 'warning'
  }>
  /** Additional styling */
  sx?: object
}

// ============================================================================
// Error Type Configuration
// ============================================================================

const ERROR_CONFIGS: Record<ErrorType, {
  icon: React.ReactNode
  defaultTitle: string
  defaultMessage: string
  color: string
}> = {
  network: {
    icon: <NetworkIcon />,
    defaultTitle: 'Connection Error',
    defaultMessage: 'Unable to connect to the server. Please check your internet connection.',
    color: poolwerxColors.error
  },
  api: {
    icon: <BugReportIcon />,
    defaultTitle: 'API Error',
    defaultMessage: 'An error occurred while fetching data from the server.',
    color: poolwerxColors.error
  },
  validation: {
    icon: <WarningIcon />,
    defaultTitle: 'Validation Error',
    defaultMessage: 'The provided data is invalid. Please check and try again.',
    color: poolwerxColors.warning
  },
  permission: {
    icon: <ErrorIcon />,
    defaultTitle: 'Access Denied',
    defaultMessage: 'You do not have permission to access this resource.',
    color: poolwerxColors.error
  },
  generic: {
    icon: <ErrorIcon />,
    defaultTitle: 'Error',
    defaultMessage: 'An unexpected error occurred. Please try again.',
    color: poolwerxColors.error
  }
}

// ============================================================================
// Main Component
// ============================================================================

const ErrorState: React.FC<ErrorStateProps> = ({
  message,
  title,
  severity = 'error',
  errorType = 'generic',
  details,
  errorCode,
  showRetry = true,
  retryText = 'Try Again',
  onRetry,
  showDetails = false,
  actions = [],
  sx = {}
}) => {
  const [showErrorDetails, setShowErrorDetails] = React.useState(false)
  
  const errorConfig = ERROR_CONFIGS[errorType]
  const displayTitle = title || errorConfig.defaultTitle
  const displayMessage = message || errorConfig.defaultMessage
  
  // Determine alert severity based on error type
  const alertSeverity = errorType === 'validation' ? 'warning' : severity
  
  return (
    <Box sx={{ ...sx }}>
      <Alert 
        severity={alertSeverity}
        icon={errorConfig.icon}
        sx={{
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <AlertTitle sx={{ fontWeight: 'bold' }}>
          {displayTitle}
          {errorCode && (
            <Chip 
              label={`Error ${errorCode}`}
              size="small" 
              variant="outlined" 
              sx={{ ml: 1, fontSize: '0.75rem' }}
            />
          )}
        </AlertTitle>
        
        <Typography variant="body2" sx={{ mb: details || actions.length > 0 ? 2 : 0 }}>
          {displayMessage}
        </Typography>
        
        {/* Error Details */}
        {details && showDetails && (
          <>
            <Button
              size="small"
              onClick={() => setShowErrorDetails(!showErrorDetails)}
              startIcon={<ExpandMoreIcon sx={{ 
                transform: showErrorDetails ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s'
              }} />}
              sx={{ mb: 1 }}
            >
              {showErrorDetails ? 'Hide Details' : 'Show Details'}
            </Button>
            
            <Collapse in={showErrorDetails}>
              <Card variant="outlined" sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography 
                    variant="body2" 
                    component="pre"
                    sx={{ 
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {details}
                  </Typography>
                </CardContent>
              </Card>
            </Collapse>
          </>
        )}
        
        {/* Action Buttons */}
        {(showRetry || actions.length > 0) && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {showRetry && onRetry && (
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                color={severity === 'error' ? 'error' : 'primary'}
              >
                {retryText}
              </Button>
            )}
            
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'outlined'}
                size="small"
                onClick={action.onClick}
                color={action.color || 'primary'}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        )}
      </Alert>
    </Box>
  )
}

export default ErrorState

// ============================================================================
// Specialized Error Components
// ============================================================================

/**
 * 🌐 Network connection error
 */
export const NetworkErrorState: React.FC<{
  message?: string;
  onRetry?: () => void;
}> = ({ message, onRetry }) => (
  <ErrorState
    errorType="network"
    message={message}
    onRetry={onRetry}
    showRetry={!!onRetry}
  />
)

/**
 * 🔒 Permission/access denied error
 */
export const PermissionErrorState: React.FC<{
  message?: string;
  resource?: string;
}> = ({ message, resource }) => (
  <ErrorState
    errorType="permission"
    title="Access Denied"
    message={message || `You don't have permission to access ${resource || 'this resource'}.`}
    showRetry={false}
    actions={[
      {
        label: 'Contact Support',
        onClick: () => window.open('mailto:support@poolwerx.com.au'),
        variant: 'outlined'
      }
    ]}
  />
)

/**
 * 🔍 Data not found error
 */
export const NotFoundErrorState: React.FC<{
  message?: string;
  resource?: string;
  onGoBack?: () => void;
}> = ({ message, resource = 'resource', onGoBack }) => (
  <ErrorState
    severity="warning"
    title="Not Found"
    message={message || `The requested ${resource} could not be found.`}
    showRetry={false}
    actions={onGoBack ? [
      {
        label: 'Go Back',
        onClick: onGoBack,
        variant: 'contained',
        color: 'primary'
      }
    ] : []}
  />
)

/**
 * 🐛 API/Server error
 */
export const ServerErrorState: React.FC<{
  message?: string;
  errorCode?: string | number;
  details?: string;
  onRetry?: () => void;
}> = ({ message, errorCode, details, onRetry }) => (
  <ErrorState
    errorType="api"
    message={message}
    errorCode={errorCode}
    details={details}
    showDetails={!!details}
    onRetry={onRetry}
    showRetry={!!onRetry}
  />
)

/**
 * ⚠️ Validation error for forms
 */
export const ValidationErrorState: React.FC<{
  message?: string;
  errors?: string[];
}> = ({ message, errors = [] }) => (
  <ErrorState
    errorType="validation"
    severity="warning"
    message={message}
    showRetry={false}
    details={errors.length > 0 ? errors.join('\n') : undefined}
    showDetails={errors.length > 0}
  />
)